/**
 * Member domain — TanStack Query keys and fetchers.
 */
import { apiFetch } from '../../lib/api/client.js';
import { tenantHeaderValue } from '../../stores/tenant.js';
import { apiRequest } from '../../lib/api/host-pool.js';

export const MEMBER_QUERY_KEYS = {
  me: ['member', 'me'] as const,
  bankAccounts: (memberId: string) => ['member', 'bankAccounts', memberId] as const,
} as const;

export interface MemberMe {
  id: string;
  username: string;
  agentId: string | null;
  phone: string | null;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  lineId: string | null;
  /**
   * HIDDEN rather than masked when the session is an impersonation one: the key
   * is absent from the payload entirely, not null (ADR-0003). Optional so the
   * type says what the wire says.
   */
  birthDate?: string | null;
  preferredLanguage: string | null;
  membershipLevel: number | null;
  status: string;
  createdAt: string;
  referralCode?: string | null;
  /**
   * Chatwoot HMAC identity hash (HMAC-SHA256(secret, id)). Present only when
   * the backend has CHATWOOT_HMAC_SECRET set (identity validation enabled);
   * forwarded to the widget as `identifier_hash`.
   */
  chatwootIdentifierHash?: string | null;
}

export interface MemberMeResponse {
  member: MemberMe;
  groups: unknown[];
  bankAccounts: unknown[];
}

export function fetchMemberMe(): Promise<MemberMeResponse> {
  return apiFetch<MemberMeResponse>('/members/me');
}

export interface PatchMemberArgs {
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  lineId?: string | null;
  preferredLanguage?: string | null;
  membershipLevel?: number;
}

export function patchMemberMe(
  memberId: string,
  patch: PatchMemberArgs,
): Promise<MemberMe> {
  return apiFetch<MemberMe>(`/members/${memberId}`, {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(patch),
  });
}

export interface ChangePasswordArgs {
  subjectId: string;
  currentPassword: string;
  newPassword: string;
}

export async function changePasswordMutation(args: ChangePasswordArgs): Promise<void> {
  await apiFetch<void>('/auth/change-password', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      subjectKind: 'member',
      subjectId: args.subjectId,
      currentPassword: args.currentPassword,
      newPassword: args.newPassword,
    }),
  });
}

// ── Bank accounts ─────────────────────────────────────────────────────────────

export interface BankAccount {
  id: string;
  memberId: string;
  bankCode: string;
  accountNumber: string;
  accountName: string;
  isDefault: boolean;
  verified: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface BankAccountsResponse {
  items: BankAccount[];
}

export function fetchBankAccounts(memberId: string): Promise<BankAccountsResponse> {
  return apiFetch<BankAccountsResponse>(`/members/${memberId}/bank-accounts`);
}

export interface CreateBankAccountArgs {
  bankCode: string;
  accountNumber: string;
  accountName: string;
  isDefault?: boolean;
}

export function createBankAccount(
  memberId: string,
  args: CreateBankAccountArgs,
): Promise<BankAccount> {
  return apiFetch<BankAccount>(`/members/${memberId}/bank-accounts`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(args),
  });
}

export function setDefaultBankAccount(
  memberId: string,
  bankId: string,
): Promise<BankAccount> {
  return apiFetch<BankAccount>(`/members/${memberId}/bank-accounts/${bankId}`, {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ isDefault: true }),
  });
}

/** Plain fetch without auto-refresh — used by the auth flow before the store is populated. */
export async function fetchMemberMeWithToken(
  accessToken: string,
): Promise<MemberMeResponse | null> {
  try {
    const tid = tenantHeaderValue();
    const headers: Record<string, string> = { authorization: `Bearer ${accessToken}` };
    if (tid) headers['x-tenant-id'] = tid;
    const res = await apiRequest(`/members/me`, {
      credentials: 'include',
      headers,
    });
    if (!res.ok) return null;
    return res.json() as Promise<MemberMeResponse>;
  } catch {
    return null;
  }
}
